import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ShieldCheck,
  Sparkles,
  Rocket,
  Search,
  PenTool,
  CheckCircle2,
} from "lucide-react";
import WhatsAppButton from "@/components/WhatsAppButton";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_creamos-webs/artifacts/ako2u2d9_ImagenLaptopBonita.png";
const EX1 =
  "https://images.unsplash.com/photo-1519222970733-f546218fa6d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBzbGVlayUyMHdlYnNpdGUlMjBkZXNpZ24lMjBvbiUyMHNjcmVlbnxlbnwwfHx8fDE3ODI1MTg5ODZ8MA&ixlib=rb-4.1.0&q=85";
const EX2 =
  "https://images.unsplash.com/photo-1602576666092-bf6447a729fc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBzbGVlayUyMHdlYnNpdGUlMjBkZXNpZ24lMjBvbiUyMHNjcmVlbnxlbnwwfHx8fDE3ODI1MTg5ODZ8MA&ixlib=rb-4.1.0&q=85";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const SectionTitle = ({ eyebrow, title, align = "left" }) => (
  <div className={align === "center" ? "text-center" : "text-left"}>
    <div className="flex items-center gap-3 mb-4 justify-start">
      {align === "center" ? null : <span className="inline-block w-12 h-px bg-[#D4AF37]" />}
      <span className="text-xs tracking-[0.3em] uppercase text-[#D4AF37]">{eyebrow}</span>
    </div>
    <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-white tracking-tight leading-tight">
      {title}
    </h2>
  </div>
);

const reasons = [
  {
    icon: ShieldCheck,
    title: "Confianza profesional",
    desc:
      "Diseños limpios y elegantes que transmiten seriedad y credibilidad para que tus clientes confíen en tu negocio desde el primer clic.",
  },
  {
    icon: Sparkles,
    title: "Diseño a la medida",
    desc:
      "Nada de plantillas genéricas. Creamos una identidad visual única, alineada con tu marca, tu público y tus objetivos comerciales.",
  },
  {
    icon: Rocket,
    title: "Resultados que importan",
    desc:
      "Sitios rápidos, optimizados para móvil y pensados para convertir visitantes en contactos reales que hacen crecer tu negocio.",
  },
];

const steps = [
  {
    n: "01",
    icon: Search,
    title: "Entendemos tu negocio",
    desc:
      "Conversamos contigo para conocer tu marca, tus clientes y tus metas. Definimos el alcance y el mensaje clave del sitio.",
  },
  {
    n: "02",
    icon: PenTool,
    title: "Diseñamos una propuesta",
    desc:
      "Creamos una propuesta visual profesional con la estructura, los textos y el estilo que mejor representen a tu negocio.",
  },
  {
    n: "03",
    icon: CheckCircle2,
    title: "Publicamos y afinamos",
    desc:
      "Lanzamos tu sitio en línea y lo ajustamos para que cargue rápido, se vea perfecto en móvil y atraiga más clientes.",
  },
];

const Home = () => {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-[#D4AF37]/10 blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-20 md:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center min-h-[70vh]">
            <motion.div
              initial="hidden"
              animate="show"
              variants={fadeUp}
            >
              <div className="flex items-center gap-3 mb-6">
                <span className="inline-block w-12 h-px bg-[#D4AF37]" />
                <span className="text-xs tracking-[0.3em] uppercase text-[#D4AF37]">
                  Diseño web profesional
                </span>
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-white">
                Creamos páginas web profesionales para que tu negocio{" "}
                <span className="text-[#D4AF37]">genere confianza</span> y
                consiga <span className="text-[#D4AF37]">más clientes</span>.
              </h1>
              <p className="mt-6 text-zinc-300 text-base md:text-lg leading-relaxed max-w-xl">
                En Rincon Web diseñamos y desarrollamos sitios elegantes,
                rápidos y enfocados en resultados. Hagamos que tu marca brille
                en línea.
              </p>

              <div className="mt-10 flex flex-wrap gap-4">
                <WhatsAppButton
                  testId="hero-wa-primary"
                  label="Hablar por WhatsApp"
                  variant="primary"
                />
                <Link
                  data-testid="hero-cta-packages"
                  to="/paquetes"
                  className="inline-flex items-center gap-2 px-8 py-4 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all"
                >
                  Ver paquetes <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="mt-10 flex items-center gap-6 text-zinc-400 text-sm">
                <div>
                  <div className="font-display text-2xl text-[#D4AF37]">100%</div>
                  <div>responsive</div>
                </div>
                <div className="w-px h-8 bg-white/10" />
                <div>
                  <div className="font-display text-2xl text-[#D4AF37]">3</div>
                  <div>pasos para lanzar</div>
                </div>
                <div className="w-px h-8 bg-white/10" />
                <div>
                  <div className="font-display text-2xl text-[#D4AF37]">∞</div>
                  <div>atención cercana</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="absolute -inset-6 bg-gradient-to-tr from-[#D4AF37]/15 via-transparent to-transparent blur-2xl" />
              <div className="relative">
                <img
                  src={HERO_IMG}
                  alt="Laptop elegante con un sitio web profesional"
                  className="w-full h-[420px] md:h-[560px] object-contain"
                  loading="eager"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* EXAMPLES PREVIEW */}
      <section className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <SectionTitle eyebrow="Nuestro trabajo" title="Ejemplos de sitios" />
            <Link
              data-testid="home-examples-link"
              to="/ejemplos"
              className="text-[#D4AF37] hover:text-white inline-flex items-center gap-2 text-sm tracking-wide"
            >
              Ver todos los ejemplos <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              { src: EX1, title: "Sitio corporativo", tag: "Servicios" },
              { src: EX2, title: "Tienda y catálogo", tag: "E-commerce" },
            ].map((ex, i) => (
              <motion.div
                key={ex.title}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.2 }}
                custom={i}
                variants={fadeUp}
                className="group relative border border-white/10 bg-[#0a0a0a] hover:border-[#D4AF37]/60 transition-colors"
              >
                <div className="overflow-hidden">
                  <img
                    src={ex.src}
                    alt={ex.title}
                    className="w-full h-72 md:h-80 object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    loading="lazy"
                  />
                </div>
                <div className="flex items-center justify-between px-6 py-5 border-t border-white/10">
                  <div>
                    <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37]">{ex.tag}</div>
                    <div className="text-white text-lg mt-1">{ex.title}</div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#D4AF37] group-hover:translate-x-1 transition-transform" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section id="porque" className="border-t border-white/5 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <SectionTitle eyebrow="Por qué elegirnos" title="Tres razones que marcan la diferencia" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mt-12">
            {reasons.map((r, i) => {
              const Icon = r.icon;
              return (
                <motion.div
                  key={r.title}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, amount: 0.2 }}
                  custom={i}
                  variants={fadeUp}
                  data-testid={`reason-card-${i}`}
                  className="border border-white/10 bg-[#0d0d0d] p-8 md:p-10 hover:border-[#D4AF37]/50 transition-colors"
                >
                  <div className="w-12 h-12 border border-[#D4AF37]/40 flex items-center justify-center mb-6">
                    <Icon className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.8} />
                  </div>
                  <h3 className="font-display text-xl md:text-2xl text-white mb-3">{r.title}</h3>
                  <p className="text-zinc-400 leading-relaxed">{r.desc}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-12 flex flex-wrap justify-start gap-4">
            <WhatsAppButton
              testId="why-wa-cta"
              label="Quiero asesoría gratis"
              variant="primary"
            />
          </div>
        </div>
      </section>

      {/* HOW WE WORK */}
      <section id="como" className="border-t border-white/5 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <SectionTitle eyebrow="Cómo trabajamos" title="Un proceso claro en 3 pasos" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mt-12">
            {steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.n}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, amount: 0.2 }}
                  custom={i}
                  variants={fadeUp}
                  data-testid={`step-card-${i}`}
                  className="relative border border-white/10 bg-[#0d0d0d] p-8 md:p-10 hover:border-[#D4AF37]/50 transition-colors"
                >
                  <div className="absolute top-6 right-6 font-display text-5xl md:text-6xl text-[#D4AF37]/30 leading-none">
                    {s.n}
                  </div>
                  <div className="w-12 h-12 border border-[#D4AF37]/40 flex items-center justify-center mb-6">
                    <Icon className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.8} />
                  </div>
                  <h3 className="font-display text-xl md:text-2xl text-white mb-3">{s.title}</h3>
                  <p className="text-zinc-400 leading-relaxed">{s.desc}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-14 border border-[#D4AF37]/40 bg-[#0a0a0a] p-8 md:p-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-2">¿Listo para empezar?</div>
              <h3 className="font-display text-2xl md:text-3xl text-white">
                Hablemos hoy y lleva tu negocio a otro nivel.
              </h3>
            </div>
            <div className="flex flex-wrap gap-4">
              <WhatsAppButton
                testId="how-wa-cta"
                label="Escríbenos por WhatsApp"
                variant="primary"
              />
              <Link
                data-testid="how-cta-contact"
                to="/contacto"
                className="inline-flex items-center gap-2 px-8 py-4 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all"
              >
                Formulario de contacto <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
